import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useNavigate } from 'react-router-dom';

import { AuthTags } from '~constants/authTags';

import { getErrorMessage } from '~libs/axiosWrapper';
import dayjs, { dateInputFieldFormat } from '~libs/dayjs';
import { useSearch } from '~libs/searchParams';

import { Button, ClickableIcon, ConfirmDialog, ContainerBanner, ErrorResponse, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import Icon, { faExclamationTriangle, faInfoCircle, faMagnifyingGlass, faPlus } from '~components/icon';
import { TableView } from '~components/table-view/';
import BulkUpdateStatus from '~support-at-home/components/budgets/BulkUpdateStatus';

import { ClientBudgetStatus } from '~support-at-home/models';

import columnsFactory from '~support-at-home/forms/budgets/budgetsList';
import searchFields from '~support-at-home/forms/budgets/budgetsSearch';

import { getAll as getAllClients } from '~main/actions/clients';
import { setTitle } from '~main/actions/login';
import { useBulkUpdateMutation, useGetBudgetsQuery, useUpdateRevisionMutation } from '~support-at-home/actions/budgets';
import { getAll as getAllClientBudgetStatuses } from '~support-at-home/actions/clientBudgetStatuses';
import { getAll as getFundingTypes } from '~support-at-home/actions/fundingTypes';
import { getAll as getPricePlans } from '~support-at-home/actions/pricePlans';
import { getAll as getAllUsers } from '~care-workers/actions';

import { selectAuthenticatedUser, selectUserTags } from '~main/selectors/login';
import {
  selectClientBudgetStatuses,
  selectClientBudgetStatusesError,
} from '~support-at-home/selectors/clientBudgetStatuses';

import BudgetBulkCopy from './bulk-copy/BudgetBulkCopy';
import BulkSahAssignWizard from './bulk-sah-assign/BulkSahAssignWizard';
import { getBulkUpdateConflictLabels } from './utils';

const Budgets: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectAuthenticatedUser);
  const userTags = useAppSelector(selectUserTags);

  const budgetStatuses = useAppSelector(selectClientBudgetStatuses);
  const budgetStatusesError = useAppSelector(selectClientBudgetStatusesError);

  const canBulkUpdate = userTags.includes(AuthTags.CLIENTS.BUDGETS.UPDATE);

  const quarterStart = dayjs().startOf('quarter').format(dateInputFieldFormat);
  const quarterEnd = dayjs().endOf('quarter').format(dateInputFieldFormat);

  const initialSearchState = {
    start_date: {
      lte: quarterEnd,
    },
    end_date: {
      gte: quarterStart,
    },
  };

  const [budgetsSelected, setBudgetsSelected] = useState<string[]>([]);
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [infoMessages, setInfoMessages] = useState<string[]>(['Showing active budgets for this current quarter only']);
  const [searchParams, setSearchParams] = useSearch('budgets-search', initialSearchState);
  const [statusReady, setStatusReady] = useState(() => !!searchParams['status.id']);

  const search = (values: Record<string, any>) => {
    setSearchParams(values);
    setInfoMessages([]);
  };

  // RTK hooks - fetch budgets & bulk update mutation
  const {
    data: budgets = [],
    isFetching: isLoading,
    error: budgetsError,
    refetch,
  } = useGetBudgetsQuery(searchParams, { skip: !statusReady });
  const [bulkUpdateBudgets, { error: bulkUpdateError }] = useBulkUpdateMutation();
  const [updateRevision, { error: updateRevisionError }] = useUpdateRevisionMutation();

  // Surface the specific budgets the API flagged as conflicting so users can resolve them (TAV-2410)
  const conflictLabels = useMemo(
    () => getBulkUpdateConflictLabels(bulkUpdateError, budgets),
    [bulkUpdateError, budgets],
  );

  // When the active error is a bulk-update conflict we already detail in the banner below, suppress
  // only that bulk-update error from the generic ErrorResponse — other errors (e.g. a failed revision
  // update raised while the conflict banner is showing) must still surface (TAV-2410).
  const conflictIsActive = conflictLabels.length > 0;
  const errorToShow = budgetsError || (conflictIsActive ? null : bulkUpdateError) || updateRevisionError;
  const showGenericError = !!errorToShow;

  const openBudget = (id: string | null = null) => navigate(`${id}`);
  const openTracker = (id: string | null = null) => navigate(`${id}/tracker`);
  const openCopy = (id: string | null = null) => navigate(`${id}/copy`);

  const handleBulkUpdateBudgets = async (statusId: string) => {
    if (!budgetsSelected.length) return;
    try {
      await bulkUpdateBudgets({
        budget_ids: budgetsSelected,
        status_id: statusId,
      }).unwrap();
      setBudgetsSelected([]);
      refetch();
    } catch (err) {
      console.error('Bulk update failed', err);
    }
  };

  const [revisionConfirmId, setRevisionConfirmId] = useState<string | null>(null);

  const handleUpdateRevision = (id: string) => {
    if (isRevisionUpdating) return;
    setRevisionConfirmId(id);
  };

  const [isRevisionUpdating, setIsRevisionUpdating] = useState(false);

  const onRevisionConfirm = async (confirmed: boolean) => {
    const id = revisionConfirmId;
    setRevisionConfirmId(null);
    if (!confirmed || !id) return;
    setIsRevisionUpdating(true);
    try {
      await updateRevision({ id }).unwrap();
    } catch (err) {
      console.error('Revision update failed', err);
    } finally {
      setIsRevisionUpdating(false);
    }
  };

  const columns = columnsFactory(
    (id) => openBudget(id),
    (id) => openTracker(id),
    user,
    (id) => openCopy(id),
    handleUpdateRevision,
  );

  useEffect(() => {
    setTitle('Budgets');
    dispatch(getAllClients());
    dispatch(getAllClientBudgetStatuses());
    dispatch(getPricePlans());
    dispatch(getFundingTypes());
    dispatch(getAllUsers());
  }, []);

  useEffect(() => {
    if (budgetStatusesError && !statusReady) {
      setStatusReady(true); // proceed without statuses if error occurs
    }
    if (budgetStatuses.length > 0 && !statusReady) {
      const active = budgetStatuses.find((s: ClientBudgetStatus) => s.tag?.toLowerCase() === 'active');
      if (active) {
        setSearchParams((prev: Record<string, any>) =>
          prev['status.id'] ? prev : { ...prev, 'status.id': [active.id] },
        );
      }
      setStatusReady(true);
    }
  }, [budgetStatuses, budgetStatusesError]);

  return (
    <div className="p-2 budgets">
      {showGenericError && <ErrorResponse message={getErrorMessage(errorToShow)} />}
      {conflictLabels.length > 0 && (
        <ContainerBanner
          className="m-2"
          color="warning"
          icon={faExclamationTriangle}
          iconSize="2x"
          title="Conflicting budgets — resolve these before activating:"
          messages={conflictLabels}
          dataCy="sah-budgets-bulk-update-conflicts"
        />
      )}
      {infoMessages.length > 0 && (
        <ContainerBanner
          className="m-2"
          color="info"
          icon={faInfoCircle}
          iconSize="2x"
          messages={infoMessages}
          onClear={() => setInfoMessages([])}
        />
      )}
      <Toolbar
        id="budgets"
        rightCluster={
          <>
            <ClickableIcon
              id="budgets-search-button"
              icon={faMagnifyingGlass}
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
            />
            <BulkUpdateStatus
              selectedIds={budgetsSelected}
              canUpdate={canBulkUpdate}
              onBulkUpdate={handleBulkUpdateBudgets}
            />
            <BudgetBulkCopy canUpdate={canBulkUpdate} />
            <BulkSahAssignWizard
              canUpdate={canBulkUpdate}
              selectedIds={budgetsSelected}
              onComplete={() => {
                refetch();
                setBudgetsSelected([]);
              }}
            />
            <Button
              dataCy="budgets-new-button"
              size="sm"
              color="success"
              href={'/support-at-home/budgets/new'}
              authTag={AuthTags.CLIENTS.BUDGETS.UPDATE}
            >
              <Icon className="me-2" icon={faPlus} />
              Add New Budget
            </Button>
          </>
        }
      />
      <SearchBar isOpen={searchBarOpen} fields={searchFields} initialValues={searchParams} onSubmit={search} />
      <div className="position-relative">
        {isRevisionUpdating && <div className="revision-loading-overlay" />}
        <TableView
          id="budgets-table"
          columns={columns}
          data={budgets}
          isLoading={isLoading || !statusReady}
          emptyMessage="No budgets found"
          readonly
          striped
          canSelect
          selectedIds={budgetsSelected}
          onRowSelectionChange={(budgetIds) => setBudgetsSelected((budgetIds as (string | number)[]).map(String))}
        />
      </div>
      <ConfirmDialog
        isOpen={!!revisionConfirmId}
        messages="Update to latest price plan revision? This will recalculate all item costs."
        onClose={onRevisionConfirm}
        useList={false}
      />
    </div>
  );
};

export default Budgets;
